import {
  FaActionTracker,
  FaTrackedAttempt,
  StructuredLogger,
  inheritRequestCorrelation,
} from '@brighthub/structured-logging';
import {
  Injectable,
  DestroyRef,
  isDevMode,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { FeatureFlagKey, FeatureFlagsService } from '@brighthub/feature-flags';
import { ClientSessionService } from '@brighthub/uisession';
import { CarePlanAnalytics } from './analytics/care-plan-analytics';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { catchError, firstValueFrom, map, of } from 'rxjs';
import {
  ACTIVE_CASE_STATUSES,
  isClosedCaseStatus,
  CARE_PLAN_CASE_STATUS_LABEL,
  CARE_PLAN_MAX_ACTIVE_GOALS,
  CarePlanCase,
  CarePlanCaseStatus,
  CarePlanClosedReason,
  CarePlanGoal,
} from './care-plan.model';
import { CarePlanGroupBy, groupCases } from './cases/case-grouping';
import {
  CarePlanCaseFilters,
  DEFAULT_CASE_FILTERS,
  applyCaseFilters,
  isDefaultCaseFilters,
  isFilterMiss,
} from './cases/case-filters';
import { CarePlanSeed } from './fixtures/care-plan.fixtures';
import {
  CARE_PLAN_FIXTURE_TAXONOMY,
  CarePlanDomain,
} from './care-plan-fixture-taxonomy.token';
import { CASE_API, CaseApi } from './cases/api/case-api.token';
import {
  CaseResource,
  CaseStatusesPayload,
  SystemCodeValue,
  CaseStatusCode,
  CaseStatusTransitionResource,
  DomainResource,
  ObjectiveTypeResource,
} from './cases/api/case-api.model';
import { CARE_PLAN_CLIENT_CONTEXT } from './care-plan-client-context.token';
import {
  caseApiErrorStatus,
  caseApiErrorMessage,
} from './cases/api/case-api-error';
import { CARE_PLAN_ACCESS } from './care-plan-access.token';
import { GOAL_API } from './goals/api/goal-api.token';
import { ClientGoalCacheService } from './goals/client-goal-cache.service';
import {
  CLIENT_GOAL_LEVEL_CODE,
  GoalResource,
} from './goals/api/goal-api.model';

export interface OpenCaseInput {
  description: string;
  domain: string;
  objective: string;
  status?: CarePlanCaseStatus;
  goalIds?: string[];
}

export interface LiveOpenCaseInput {
  description: string;
  domainId: number;
  objectiveTypeId: number;
  status: CarePlanCaseStatus;
}

export interface LiveCaseStatusTransition {
  closedReason?: CarePlanClosedReason;
  closedReasonCode?: string;
  reason?: string;
}

export interface CarePlanLoadError {
  kind: 'unrecoverable';
  message: string;
}

export class CarePlanLiveWriteBlockedError extends Error {}

export type LiveGoalWrite =
  | { kind: 'create'; description: string; caseId?: string }
  | {
      kind: 'update';
      goalId: string;
      description?: string;
      statusCode?: 'COMPLETED' | 'NO_LONGER_RELEVANT';
    }
  | { kind: 'link' | 'unlink'; goalId: string; caseId: string };

export class GoalSaveError extends Error {
  constructor(
    message: string,
    readonly ambiguousCreate = false,
  ) {
    super(message);
  }
}

/**
 * Care Plan state keyed by the selected client's GUID. Application runtime
 * state comes from AIM; fixture loading is retained only for isolated tests of
 * Goal component tests.
 */
@Injectable()
export class CarePlanStore {
  private readonly _actionLogs = new FaActionTracker(inject(StructuredLogger));
  readonly goalsEnabled = toSignal(
    inject(FeatureFlagsService)
      .getValueChanges$(FeatureFlagKey.ENABLECLIENTGOALS, false)
      .pipe(
        map((value) => value === true),
        catchError(() => of(false)),
      ),
    { initialValue: false },
  );
  private readonly _session = inject(ClientSessionService);
  private readonly _goalApi = inject(GOAL_API, { optional: true });
  private readonly _goalCache = inject(ClientGoalCacheService);
  private readonly _analytics = inject(CarePlanAnalytics);
  private readonly _destroyRef = inject(DestroyRef);
  private _goalGeneration = 0;
  private _goalKey: string | null = null;
  private _goalRequest: Promise<void> | null = null;
  private readonly _goalsLoading = signal(false);
  private readonly _goalsLoaded = signal(false);
  private readonly _goalsError = signal<string | null>(null);
  readonly goalsLoading = this._goalsLoading.asReadonly();
  readonly goalsLoaded = this._goalsLoaded.asReadonly();
  readonly goalsError = this._goalsError.asReadonly();
  private readonly _goalsSaving = signal(false);
  readonly goalsSaving = this._goalsSaving.asReadonly();
  readonly canManageGoals = computed(
    () =>
      this.goalsEnabled() &&
      (!this._isLive() ||
        (this.canCreateLiveCase() &&
          this._goalsLoaded() &&
          !this._goalsLoading() &&
          !this._goalsError() &&
          !this._goalsSaving() &&
          !this._goalCache.isWriting(
            this.sessionGuid(),
            this._clientContext?.clientId(),
          ))),
  );
  private readonly _clientContext = inject(CARE_PLAN_CLIENT_CONTEXT, {
    optional: true,
  });
  private readonly _caseApi = inject<CaseApi>(CASE_API, { optional: true });
  private readonly _fixtureTaxonomy = inject(CARE_PLAN_FIXTURE_TAXONOMY, {
    optional: true,
  });
  private readonly _access = inject(CARE_PLAN_ACCESS, { optional: true });

  private readonly _activeGuid = signal<string | null>(null);
  private readonly _cases = signal<CarePlanCase[]>([]);
  private readonly _goals = signal<CarePlanGoal[]>([]);
  private readonly _loading = signal(false);
  private readonly _error = signal<CarePlanLoadError | null>(null);
  private readonly _loadWarning = signal<string | null>(null);
  private readonly _groupBy = signal<CarePlanGroupBy>('status');
  private readonly _filters = signal<CarePlanCaseFilters>(DEFAULT_CASE_FILTERS);
  private readonly _expandedCaseId = signal<string | null>(null);
  private readonly _isLive = signal(true);
  private readonly _liveReload = signal(0);
  private readonly _domains = signal<DomainResource[]>([]);
  private readonly _domainsLoaded = signal(false);
  private readonly _domainLoading = signal(false);
  private readonly _domainError = signal<string | null>(null);
  private readonly _objectiveTypes = signal<
    Record<number, ObjectiveTypeResource[]>
  >({});
  private readonly _objectiveTypeLoading = signal<ReadonlySet<number>>(
    new Set(),
  );
  private readonly _objectiveTypeErrors = signal<Record<number, string>>({});
  private readonly _caseStatusReference = signal<CaseStatusesPayload | null>(
    null,
  );
  private readonly _caseStatusLoading = signal(false);
  private readonly _caseStatusError = signal<string | null>(null);
  readonly caseStatusReference = this._caseStatusReference.asReadonly();
  readonly caseStatusLoading = this._caseStatusLoading.asReadonly();
  readonly caseStatusError = this._caseStatusError.asReadonly();
  private readonly _closedReasons = signal<SystemCodeValue[] | null>(null);
  private readonly _closedReasonsLoading = signal(false);
  private readonly _closedReasonsError = signal<string | null>(null);
  readonly closedReasons = this._closedReasons.asReadonly();
  readonly closedReasonsLoading = this._closedReasonsLoading.asReadonly();
  readonly closedReasonsError = this._closedReasonsError.asReadonly();
  private _loadGeneration = 0;

  readonly caseGoalIds = computed(() => {
    const links = new Map<string, string[]>();
    if (!this.goalsEnabled()) return links;
    for (const goal of this._goals()) {
      for (const caseId of goal.linkedCaseIds) {
        links.set(caseId, [...(links.get(caseId) ?? []), goal.id]);
      }
    }
    return links;
  });
  readonly cases = computed(() =>
    this._isLive()
      ? this._cases().map((item) => ({
          ...item,
          goalIds: this.caseGoalIds().get(item.id) ?? [],
        }))
      : this._cases(),
  );
  readonly goals = this._goals.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly loadWarning = this._loadWarning.asReadonly();
  readonly groupBy = this._groupBy.asReadonly();
  readonly filters = this._filters.asReadonly();
  readonly activeGuid = this._activeGuid.asReadonly();
  readonly isLive = this._isLive.asReadonly();
  readonly domains = this._domains.asReadonly();
  readonly domainsLoaded = this._domainsLoaded.asReadonly();
  readonly domainLoading = this._domainLoading.asReadonly();
  readonly domainError = this._domainError.asReadonly();

  /** Case whose detail is expanded in the list; at most one at a time (D11). */
  readonly expandedCaseId = this._expandedCaseId.asReadonly();

  readonly sessionGuid = computed(
    () => this._session.selectedSubjectData()?.guid?.trim() || null,
  );

  readonly canCreateLiveCase = computed(() => {
    const sessionGuid = this.sessionGuid();
    const clientId = this._clientContext?.clientId() ?? null;
    return (
      this.canEditLiveCases() &&
      !!sessionGuid &&
      this._clientContext?.activeGuid() === sessionGuid &&
      Number.isSafeInteger(clientId) &&
      Number(clientId) > 0
    );
  });

  readonly canViewLiveCases = (): boolean => this._access?.canView() ?? false;
  readonly canEditLiveCases = (): boolean => this._access?.canEdit() ?? false;

  readonly activeGoals = computed(() =>
    (this.goalsEnabled() ? [...this._goals()] : [])
      .filter((g) => g.status === 'active')
      .sort((a, b) => a.sortOrder - b.sortOrder),
  );

  statusLabel(status: string): string {
    return (
      this.caseStatusReference()?.CASE_STATUS.find(
        (item) => item.code.toLowerCase() === status,
      )?.value ??
      CARE_PLAN_CASE_STATUS_LABEL[status] ??
      status.replace(/_/g, ' ')
    );
  }

  private statusCode(status: string): string {
    return (
      this.caseStatusReference()?.CASE_STATUS.find(
        (item) => item.code.toLowerCase() === status,
      )?.code ?? status.toUpperCase()
    );
  }

  readonly visibleCases = computed(() =>
    applyCaseFilters(this.cases(), this._filters(), this._isLive()),
  );

  readonly groupedCases = computed(() =>
    groupCases(this.visibleCases(), this._groupBy(), {
      statusLabel: (status) => this.statusLabel(status),
      domains: this._groupingDomains(),
      // Live grouping includes historical associations, using local goal order.
      goals: this._isLive() ? this.goals() : this.activeGoals(),
      includeHistoricalGoals: this._isLive(),
    }),
  );

  readonly isFilterMiss = computed(() =>
    isFilterMiss(this.cases(), this._filters(), this._isLive()),
  );

  private readonly _statusFilter = computed(() => this._filters().status);

  readonly isEmpty = computed(
    () =>
      !this._loading() &&
      !this._error() &&
      isDefaultCaseFilters(this._filters()) &&
      this.visibleCases().length === 0,
  );

  constructor() {
    effect(() => {
      const enabled = this.goalsEnabled();
      const guid = this._activeGuid();
      const clientId = this._clientContext?.clientId();
      const live = this._isLive();
      untracked(() => {
        if (!enabled) {
          this._resetGoals();
          this._goalCache.invalidate();
          if (this._groupBy() === 'goal') this._groupBy.set('status');
          if (this._filters().goalId)
            this._filters.update((filters) => ({ ...filters, goalId: null }));
        } else if (live && guid && clientId) {
          void this.ensureGoalsLoaded(guid, clientId);
        }
      });
    });
    this._destroyRef.onDestroy(() => {
      this._goalGeneration++;
    });
    this._goalCache.writesSettled
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(({ guid, clientId }) => {
        if (!this._goalClientCurrent(guid, clientId) || this._goalsSaving())
          return;
        // A replacement panel may still be reading pre-write state. Supersede
        // that request instead of coalescing it into the post-write refresh.
        this._goalRequest = null;
        void this._fetchGoals(guid, clientId);
      });
    effect(() => {
      const guid = this.sessionGuid();
      const live = this._isLive();
      // Filters belong to the currently displayed client. A direct client
      // switch must start with a complete load so duplicate detection is not
      // limited to a status slice carried over from the prior client.
      const statusFilter =
        untracked(this._activeGuid) === guid ? this._statusFilter() : null;
      this._liveReload();
      if (!guid) {
        if (live) this._reset();
        return;
      }
      if (!live) return;

      untracked(() => void this.loadCaseStatuses());
      void this.loadDomains();

      const clientId = this._clientContext?.clientId() ?? null;
      const metadataGuid = this._clientContext?.activeGuid() ?? null;
      const metadataError = this._clientContext?.error() ?? null;
      if (!clientId || metadataGuid !== guid) {
        const generation = ++this._loadGeneration;
        this._prepareLiveLoad(guid);
        if (metadataError || (metadataGuid === guid && !clientId)) {
          this._setLiveError(
            generation,
            guid,
            metadataError
              ? 'Client metadata is unavailable, so cases cannot be loaded.'
              : 'Client metadata is missing its numeric client ID, so cases cannot be loaded.',
          );
        }
        return;
      }
      void this.load(guid, clientId, statusFilter);
    });
  }

  async load(
    guid: string,
    clientId: number,
    statusFilter: CarePlanCaseStatus | null = null,
  ): Promise<void> {
    const generation = ++this._loadGeneration;
    this._prepareLiveLoad(guid);
    untracked(() => void this.ensureGoalsLoaded(guid, clientId));
    if (!this._caseApi) {
      this._setLiveError(generation, guid, 'Case API is not configured.');
      return;
    }
    this._loading.set(true);
    try {
      const resources = await this._loadCaseSlice(clientId, statusFilter);
      if (generation !== this._loadGeneration || this._activeGuid() !== guid)
        return;
      const unsupportedStatusCount = resources.filter(
        (resource) => !isSupportedCaseStatus(resource.statusCode),
      ).length;
      const cases = resources
        .filter((resource) => isSupportedCaseStatus(resource.statusCode))
        .map(toCarePlanCase);
      this._cases.set(this._mergeCaseSlice(cases, statusFilter));
      this._loadWarning.set(
        unsupportedStatusCount > 0
          ? `${unsupportedStatusCount} ${unsupportedStatusCount === 1 ? 'case was' : 'cases were'} not displayed because AIM returned an unsupported status.`
          : null,
      );
      this._loading.set(false);
      this._hydrateCaseReferenceLabels();
      for (const domainId of new Set(
        this._cases()
          .map((item) => item.domainId)
          .filter((id): id is number => id !== undefined),
      )) {
        void this.loadObjectiveTypes(domainId);
      }
    } catch (error) {
      this._setLiveError(
        generation,
        guid,
        error instanceof Error
          ? error.message
          : "This client's care plan can't be loaded right now",
      );
    }
  }

  /** Loaded only by the panel-scoped store, when Care Plan is actually opened. */
  ensureGoalsLoaded(guid: string, clientId: number): Promise<void> {
    const key = `${guid}:${clientId}`;
    if (!this._goalClientCurrent(guid, clientId)) return Promise.resolve();
    if (this._goalKey === key) return this._goalRequest ?? Promise.resolve();
    const cached = this._goalCache.read(guid, clientId);
    if (cached) {
      this._goalKey = key;
      this._goals.set(cached);
      this._goalsLoaded.set(true);
      return Promise.resolve();
    }
    return this._fetchGoals(guid, clientId);
  }

  /** Individual AIM writes, with an acknowledgement for every committed operation. */
  saveLiveGoals(
    changes: readonly LiveGoalWrite[],
    applied: (change: LiveGoalWrite, createdId?: string) => void,
    observe = true,
    interactionKey?: string,
  ): Promise<void> {
    if (!observe || changes.length === 0)
      return this._saveLiveGoals(changes, applied);
    const change = changes[0];
    const name =
      interactionKey || changes.length > 1
        ? 'care_plan.goal.save_batch'
        : change.kind === 'create'
          ? 'care_plan.goal.create'
          : change.kind === 'link'
            ? 'care_plan.goal.link_case'
            : change.kind === 'unlink'
              ? 'care_plan.goal.unlink_case'
              : change.kind === 'update' && change.statusCode
                ? 'care_plan.goal.change_status'
                : 'care_plan.goal.update';
    return this._actionLogs.run(
      name,
      `${this._activeGuid()}:${interactionKey ?? ('goalId' in change ? change.goalId : 'new')}`,
      (attempt) => {
        attempt.identifiers = {
          client_guid: this._activeGuid(),
          goal_id: 'goalId' in change ? change.goalId : undefined,
        };
        attempt.stage = 'save_goals';
        attempt.operationCount = changes.length;
        return this._saveLiveGoals(changes, applied, attempt);
      },
    );
  }

  private async _saveLiveGoals(
    changes: readonly LiveGoalWrite[],
    applied: (change: LiveGoalWrite, createdId?: string) => void,
    logAttempt?: FaTrackedAttempt,
  ): Promise<void> {
    const guid = this._activeGuid();
    const clientId = this._clientContext?.clientId();
    if (
      !guid ||
      !clientId ||
      !this._goalApi ||
      !this._goalClientCurrent(guid, clientId) ||
      !this.canManageGoals()
    ) {
      throw new GoalSaveError(
        'Goals are unavailable or you no longer have edit access.',
      );
    }
    for (const change of changes) {
      if (
        'description' in change &&
        change.description !== undefined &&
        (!change.description.trim() || change.description.trim().length > 200)
      ) {
        throw new GoalSaveError('Goals must contain 1–200 characters.');
      }
      for (const id of [
        'goalId' in change ? change.goalId : undefined,
        'caseId' in change ? change.caseId : undefined,
      ]) {
        if (
          id !== undefined &&
          (!Number.isSafeInteger(Number(id)) || Number(id) <= 0)
        ) {
          throw new GoalSaveError(
            'A valid goal or case identifier is required.',
          );
        }
      }
    }
    let generation = this._goalGeneration;
    const knownGoalIds = new Set(this._goals().map((goal) => goal.id));
    // Acknowledged membership is independent of intermediate reconciliation reads.
    // Each retry starts from the refreshed cache, without recounting prior writes.
    const activeGoalIds = new Set(this.activeGoals().map((goal) => goal.id));
    const acknowledge = (change: LiveGoalWrite, createdId?: string) => {
      if (change.kind === 'create' && createdId) activeGoalIds.add(createdId);
      else if (change.kind === 'update' && change.statusCode)
        activeGoalIds.delete(change.goalId);
      if (logAttempt) logAttempt.completedCount++;
      applied(change, createdId);
      if (change.kind === 'create' && createdId)
        this._analytics.goalCreated(createdId, activeGoalIds.size, guid);
    };
    const finishWrite = this._goalCache.beginWrite(guid, clientId);
    this._goalsSaving.set(true);
    let writeError: unknown;
    try {
      for (const change of changes) {
        if (logAttempt) logAttempt.dispatched = false;
        if (
          generation !== this._goalGeneration ||
          !this._goalClientCurrent(guid, clientId) ||
          !this.canEditLiveCases()
        ) {
          throw new GoalSaveError(
            'The selected client or your edit access changed.',
          );
        }
        try {
          let createdId: string | undefined;
          if (change.kind === 'create') {
            if (logAttempt) logAttempt.dispatched = true;
            const resource: GoalResource = await firstValueFrom(
              this._goalApi.create({
                clientId,
                description: change.description.trim(),
                goalLevelCode: CLIENT_GOAL_LEVEL_CODE,
                ...(change.caseId ? { caseId: Number(change.caseId) } : {}),
              }),
            );
            if (
              !Number.isSafeInteger(resource.goalId) ||
              resource.goalId <= 0 ||
              resource.clientId !== clientId
            ) {
              throw new Error('The created goal could not be confirmed.');
            }
            createdId = String(resource.goalId);
          } else if (change.kind === 'update') {
            const original = this._goals().find(
              (goal) => goal.id === change.goalId,
            );
            if (!original || original.status !== 'active')
              throw new GoalSaveError(
                'This goal is no longer active. Reload goals before editing.',
              );
            if (logAttempt) logAttempt.dispatched = true;
            await firstValueFrom(
              this._goalApi.update(Number(change.goalId), {
                ...(change.description !== undefined
                  ? { description: change.description.trim() }
                  : {}),
                ...(change.statusCode
                  ? {
                      statusCode: change.statusCode,
                      expectedCurrentStatusCode: 'ACTIVE',
                    }
                  : {}),
              }),
            );
          } else {
            if (logAttempt) logAttempt.dispatched = true;
            await firstValueFrom(
              this._goalApi[change.kind](
                Number(change.goalId),
                Number(change.caseId),
              ),
            );
          }
          // The UI records confirmed work even if navigation occurred during the request.
          if (createdId) knownGoalIds.add(createdId);
          acknowledge(change, createdId);
        } catch (error) {
          const detail = error as {
            status?: number;
            code?: string;
            error?: { result?: { code?: string } };
          };
          const code = detail?.error?.result?.code ?? detail?.code;
          // An existing pair already satisfies a requested link (including retry after timeout).
          if (change.kind === 'link' && code === 'LINK_EXISTS') {
            acknowledge(change);
            continue;
          }
          // A response can fail after persistence. Keep creation recovery limited
          // to observed 404 behavior; updates can also reconcile uncertain transport
          // and server failures, only when a fresh read confirms every changed field.
          if (
            (detail?.status === 404 ||
              (change.kind === 'update' &&
                (detail?.status === 0 || (detail?.status ?? 0) >= 500))) &&
            generation === this._goalGeneration &&
            this._goalClientCurrent(guid, clientId)
          ) {
            await this.refreshGoals();
            generation = this._goalGeneration;
            if (
              this._goalClientCurrent(guid, clientId) &&
              !this._goalsError()
            ) {
              if (change.kind === 'create') {
                const matches = this._goals().filter(
                  (goal) =>
                    !knownGoalIds.has(goal.id) &&
                    goal.status === 'active' &&
                    goal.displayText === change.description.trim() &&
                    (!change.caseId ||
                      goal.linkedCaseIds.includes(change.caseId)),
                );
                if (matches.length === 1) {
                  knownGoalIds.add(matches[0].id);
                  acknowledge(change, matches[0].id);
                  continue;
                }
              } else {
                const refreshed = this._goals().find(
                  (goal) => goal.id === change.goalId,
                );
                const confirmed =
                  refreshed &&
                  (change.kind === 'update'
                    ? (change.description === undefined ||
                        refreshed.displayText === change.description.trim()) &&
                      (!change.statusCode ||
                        refreshed.status ===
                          (change.statusCode === 'COMPLETED'
                            ? 'achieved'
                            : 'abandoned'))
                    : refreshed.linkedCaseIds.includes(change.caseId) ===
                      (change.kind === 'link'));
                if (confirmed) {
                  acknowledge(change);
                  continue;
                }
              }
            }
          }
          const rejected =
            !!code &&
            ['GOAL_CAP_REACHED', 'INVALID_FIELD', 'MISSING_REQ_FIELD'].includes(
              code,
            );
          const ambiguous =
            change.kind === 'create' &&
            !rejected &&
            (detail?.status == null ||
              detail.status === 0 ||
              detail.status === 404 ||
              detail.status >= 500);
          const message =
            code === 'GOAL_CAP_REACHED'
              ? 'This client already has three active goals.'
              : code === 'GOAL_TRANSITION_CONFLICT'
                ? 'This goal changed elsewhere. Review the refreshed goals before saving again.'
                : ambiguous
                  ? 'The goal may have been created. Close this dialog and review the refreshed goals before capturing it again.'
                  : caseApiErrorMessage(
                      error,
                      'The goal change could not be saved.',
                    );
          if (logAttempt) {
            logAttempt.failure = error;
            logAttempt.uncertain = ambiguous;
          }
          throw inheritRequestCorrelation(
            new GoalSaveError(message, ambiguous),
            error,
          );
        }
      }
    } catch (error) {
      writeError = error;
    } finally {
      finishWrite();
      if (
        generation === this._goalGeneration &&
        this._goalClientCurrent(guid, clientId)
      )
        await this.refreshGoals();
      this._goalsSaving.set(false);
    }
    if (writeError) throw writeError;
    if (!this._goalClientCurrent(guid, clientId))
      throw new GoalSaveError('The selected client changed.');
    if (this._goalsError()) {
      if (logAttempt) logAttempt.stage = 'refresh';
      throw new GoalSaveError(
        'Changes were saved, but goals could not be refreshed. Close this dialog and retry loading goals.',
      );
    }
  }

  /** Explicit Retry or post-write invalidation; concurrent callers share the refresh. */
  refreshGoals(): Promise<void> {
    const guid = this._activeGuid();
    const clientId = this._clientContext?.clientId();
    if (!guid || !clientId || !this._goalClientCurrent(guid, clientId))
      return Promise.resolve();
    if (this._goalRequest) return this._goalRequest;
    this._goalCache.invalidate();
    return this._fetchGoals(guid, clientId);
  }

  private _goalClientCurrent(guid: string, clientId: number): boolean {
    return (
      !this._destroyRef.destroyed &&
      this.goalsEnabled() &&
      this._isLive() &&
      this.canViewLiveCases() &&
      this.sessionGuid() === guid &&
      this._activeGuid() === guid &&
      this._clientContext?.activeGuid() === guid &&
      this._clientContext?.clientId() === clientId
    );
  }

  private _fetchGoals(guid: string, clientId: number): Promise<void> {
    const generation = ++this._goalGeneration;
    this._goalKey = `${guid}:${clientId}`;
    this._goalsLoading.set(true);
    this._goalsError.set(null);
    const current = () =>
      generation === this._goalGeneration &&
      this._goalClientCurrent(guid, clientId);
    const request = (async () => {
      try {
        if (!this._goalApi) throw new Error('Goal API is not configured.');
        const resources = new Map<number, GoalResource>();
        let offset = 0;
        while (true) {
          const page = await firstValueFrom(
            this._goalApi.list({ clientId, limit: 100, offset }),
          );
          if (!current()) return;
          if (
            !Array.isArray(page?.items) ||
            typeof page?.resultSet?.allFetched !== 'boolean'
          ) {
            throw new Error('Goal API returned an invalid list response.');
          }
          const before = resources.size;
          for (const resource of page.items) {
            if (
              resource.clientId !== clientId ||
              !Number.isSafeInteger(resource.goalId) ||
              resource.goalId <= 0 ||
              !Array.isArray(resource.caseIds) ||
              resource.caseIds.some(
                (id) => !Number.isSafeInteger(id) || id <= 0,
              ) ||
              typeof resource.description !== 'string' ||
              (resource.originalDescription !== null &&
                typeof resource.originalDescription !== 'string') ||
              ![
                'ACTIVE',
                'COMPLETED',
                'NO_LONGER_RELEVANT',
                'ACHIEVED',
                'ABANDONED',
                'REVISED',
              ].includes(resource.statusCode)
            ) {
              throw new Error('Goal API returned an invalid goal.');
            }
            resources.set(resource.goalId, resource);
          }
          // AIM uses the shared resultSet contract, not the EDD's flat count/offset.
          if (page.resultSet.allFetched) break;
          const reportedOffset = page.resultSet.offset;
          if (!Number.isSafeInteger(reportedOffset) || reportedOffset < 0) {
            throw new Error(
              'Goal API returned an invalid continuation offset.',
            );
          }
          const nextOffset =
            reportedOffset > offset
              ? reportedOffset
              : offset + page.items.length;
          if (nextOffset <= offset || resources.size === before) {
            throw new Error('Goal pagination did not advance.');
          }
          offset = nextOffset;
        }
        const priorOrder = new Map(
          this._goals().map((goal) => [goal.id, goal.sortOrder]),
        );
        let nextOrder = Math.max(-1, ...priorOrder.values()) + 1;
        this._goals.set(
          [...resources.values()].map((goal) => ({
            id: String(goal.goalId),
            displayText: goal.description,
            originalDescription: goal.originalDescription,
            status:
              goal.statusCode === 'COMPLETED'
                ? 'achieved'
                : goal.statusCode === 'NO_LONGER_RELEVANT'
                  ? 'abandoned'
                  : (goal.statusCode.toLowerCase() as CarePlanGoal['status']),
            sortOrder: priorOrder.get(String(goal.goalId)) ?? nextOrder++,
            linkedCaseIds: [...new Set(goal.caseIds)].map(String),
          })),
        );
        this._goalsLoaded.set(true);
        this._goalCache.write(guid, clientId, this._goals());
      } catch (error) {
        if (current())
          this._goalsError.set(
            error instanceof Error
              ? error.message
              : 'Goals could not be loaded.',
          );
      } finally {
        if (generation === this._goalGeneration) {
          this._goalsLoading.set(false);
          this._goalRequest = null;
        }
      }
    })();
    this._goalRequest = request;
    // A missing provider can fail synchronously before the first await.
    void request.then(() => {
      if (this._goalRequest === request) this._goalRequest = null;
    });
    return request;
  }

  private _resetGoals(): void {
    this._goalGeneration++;
    this._goalKey = null;
    this._goalRequest = null;
    this._goals.set([]);
    this._goalsLoading.set(false);
    this._goalsLoaded.set(false);
    this._goalsError.set(null);
  }

  /** @internal Test-only seam for exercising live behavior after a fixture seed. */
  useLive(): void {
    this._isLive.set(true);
    this._liveReload.update((value) => value + 1);
  }

  retryLive(): void {
    if (!this._isLive()) return;
    if (this._domainError()) void this.loadDomains(true);
    if (this._caseStatusError()) void this.loadCaseStatuses(true);
    this._clientContext?.refresh();
    this._liveReload.update((value) => value + 1);
  }

  /** Active status vocabulary; ordering and transition policy belong to the UI. */
  async loadCaseStatuses(force = false): Promise<void> {
    if (!this._caseApi || this._caseStatusLoading()) return;
    if (!force && (this._caseStatusReference() || this._caseStatusError()))
      return;
    this._caseStatusLoading.set(true);
    this._caseStatusError.set(null);
    try {
      const payload = await firstValueFrom(this._caseApi.listCaseStatuses());
      if (
        !Array.isArray(payload.CASE_STATUS) ||
        !payload.CASE_STATUS.length ||
        payload.CASE_STATUS.some((item) => !item.code || !item.value)
      ) {
        throw new Error('Case statuses are unavailable.');
      }
      this._caseStatusReference.set(payload);
      if (isDevMode()) {
        console.debug(
          '[Care Plan] case_status reference',
          JSON.stringify(payload),
        );
      }
    } catch (error) {
      this._caseStatusError.set(
        error instanceof Error
          ? error.message
          : 'Case statuses could not be loaded.',
      );
    } finally {
      this._caseStatusLoading.set(false);
    }
  }

  async loadClosedReasons(force = false): Promise<void> {
    if (!this._caseApi || this._closedReasonsLoading()) return;
    if (!force && (this._closedReasons() || this._closedReasonsError())) return;
    this._closedReasonsLoading.set(true);
    this._closedReasonsError.set(null);
    try {
      const payload = await firstValueFrom(
        this._caseApi.listCaseClosedReasons(),
      );
      if (
        !Array.isArray(payload.CASE_CLOSED_REASON) ||
        !payload.CASE_CLOSED_REASON.length ||
        payload.CASE_CLOSED_REASON.some((item) => !item.code || !item.value)
      ) {
        throw new Error('Case closure reasons are unavailable.');
      }
      this._closedReasons.set(payload.CASE_CLOSED_REASON);
      if (isDevMode())
        console.debug(
          '[Care Plan] case_closed_reason reference',
          JSON.stringify(payload),
        );
    } catch (error) {
      this._closedReasonsError.set(
        error instanceof Error
          ? error.message
          : 'Case closure reasons could not be loaded.',
      );
    } finally {
      this._closedReasonsLoading.set(false);
    }
  }

  async loadDomains(force = false): Promise<void> {
    if (!this._caseApi || this._domainLoading()) return;
    if (!force && (this._domainsLoaded() || this._domainError())) return;
    if (force) this._domainsLoaded.set(false);
    this._domainLoading.set(true);
    this._domainError.set(null);
    try {
      const domains = await firstValueFrom(this._caseApi.listDomains());
      this._domains.set(
        [...domains].sort((a, b) => a.displayOrder - b.displayOrder),
      );
      this._domainsLoaded.set(true);
      this._hydrateCaseReferenceLabels();
    } catch (error) {
      this._domainError.set(
        error instanceof Error
          ? error.message
          : 'Case domains could not be loaded.',
      );
    } finally {
      this._domainLoading.set(false);
    }
  }

  objectiveTypesForDomain(domainId: number): ObjectiveTypeResource[] {
    return this._objectiveTypes()[domainId] ?? [];
  }

  objectiveTypesLoading(domainId: number): boolean {
    return this._objectiveTypeLoading().has(domainId);
  }

  objectiveTypesError(domainId: number): string | null {
    return this._objectiveTypeErrors()[domainId] ?? null;
  }

  async loadObjectiveTypes(domainId: number, force = false): Promise<void> {
    if (!this._caseApi || this._objectiveTypeLoading().has(domainId)) return;
    if (!force && this._objectiveTypes()[domainId]) return;

    this._objectiveTypeLoading.update((current) =>
      new Set(current).add(domainId),
    );
    this._objectiveTypeErrors.update((current) => {
      const next = { ...current };
      delete next[domainId];
      return next;
    });
    try {
      const objectiveTypes = await firstValueFrom(
        this._caseApi.listObjectiveTypes(domainId),
      );
      this._objectiveTypes.update((current) => ({
        ...current,
        [domainId]: [...objectiveTypes].sort(
          (a, b) => a.displayOrder - b.displayOrder,
        ),
      }));
      this._hydrateCaseReferenceLabels();
    } catch (error) {
      this._objectiveTypeErrors.update((current) => ({
        ...current,
        [domainId]:
          error instanceof Error
            ? error.message
            : 'Objective types could not be loaded.',
      }));
    } finally {
      this._objectiveTypeLoading.update((current) => {
        const next = new Set(current);
        next.delete(domainId);
        return next;
      });
    }
  }

  findDuplicateByReference(
    domainId: number,
    objectiveTypeId: number,
  ): CarePlanCase | null {
    return (
      this._cases().find(
        (caseRecord) =>
          ACTIVE_CASE_STATUSES.includes(caseRecord.status) &&
          caseRecord.domainId === domainId &&
          caseRecord.objectiveTypeId === objectiveTypeId,
      ) ?? null
    );
  }

  async createLiveCase(input: LiveOpenCaseInput): Promise<CarePlanCase> {
    if (!this._isLive() || !this._caseApi) {
      throw new CarePlanLiveWriteBlockedError(
        'The live Case API is not available.',
      );
    }
    if (!this.canEditLiveCases()) {
      throw new CarePlanLiveWriteBlockedError(
        'Care Plan edit access is required to open a case.',
      );
    }
    const clientId = this._clientContext?.clientId() ?? null;
    const requestGuid = this.sessionGuid();
    const metadataGuid = this._clientContext?.activeGuid() ?? null;
    if (!clientId || !requestGuid || metadataGuid !== requestGuid) {
      throw new CarePlanLiveWriteBlockedError(
        'Client metadata is unavailable, so the case cannot be opened.',
      );
    }

    const resource = await firstValueFrom(
      this._caseApi.createCase({
        clientId,
        domainId: input.domainId,
        objectiveTypeId: input.objectiveTypeId,
        statusCode: input.status.toUpperCase(),
        description: input.description,
      }),
    );
    const created = toCarePlanCase({
      ...resource,
      description: resource.description ?? input.description,
    });
    if (
      !this._isLive() ||
      this.sessionGuid() !== requestGuid ||
      this._activeGuid() !== requestGuid ||
      this._clientContext?.activeGuid() !== requestGuid ||
      this._clientContext?.clientId() !== clientId
    ) {
      return created;
    }
    this._cases.update((cases) => [created, ...cases]);
    this._hydrateCaseReferenceLabels();
    return this._cases().find((item) => item.id === created.id) ?? created;
  }

  private readonly _caseWrites = signal<ReadonlySet<string>>(new Set());

  isCaseSaving(caseId: string): boolean {
    return this._caseWrites().has(caseId);
  }

  private async _withCaseWrite<T>(
    caseId: string,
    write: () => Promise<T>,
  ): Promise<T> {
    if (this.isCaseSaving(caseId)) {
      throw new CarePlanLiveWriteBlockedError(
        'This case is already being saved. Please wait.',
      );
    }
    this._caseWrites.update((current) => new Set([...current, caseId]));
    try {
      return await write();
    } finally {
      this._caseWrites.update((current) => {
        const remaining = new Set(current);
        remaining.delete(caseId);
        return remaining;
      });
    }
  }

  transitionLiveCaseStatus(
    caseId: string,
    toStatus: CarePlanCaseStatus,
    options: LiveCaseStatusTransition = {},
  ): Promise<CarePlanCase> {
    return this._withCaseWrite(caseId, () =>
      this._transitionLiveCaseStatus(caseId, toStatus, options),
    );
  }

  updateLiveCaseDescription(
    caseId: string,
    description: string,
  ): Promise<void> {
    return this._withCaseWrite(caseId, () =>
      this._updateLiveCaseDescription(caseId, description),
    );
  }

  private async _transitionLiveCaseStatus(
    caseId: string,
    toStatus: CarePlanCaseStatus,
    options: LiveCaseStatusTransition = {},
  ): Promise<CarePlanCase> {
    return this._actionLogs.run(
      'care_plan.case.change_status',
      `${this.sessionGuid()}:${caseId}`,
      async (logAttempt) => {
        logAttempt.identifiers = {
          client_guid: this.sessionGuid(),
          case_id: caseId,
        };
        logAttempt.stage = 'save';
        if (!this._isLive() || !this._caseApi) {
          throw new CarePlanLiveWriteBlockedError(
            'The live Case API is not available.',
          );
        }
        if (!this.canEditLiveCases()) {
          throw new CarePlanLiveWriteBlockedError(
            'Care Plan edit access is required to change case status.',
          );
        }
        const current = this._cases().find((item) => item.id === caseId);
        const numericCaseId = Number(caseId);
        const clientId = this._clientContext?.clientId() ?? null;
        const requestGuid = this.sessionGuid();
        const metadataGuid = this._clientContext?.activeGuid() ?? null;
        if (
          !current ||
          !Number.isSafeInteger(numericCaseId) ||
          !clientId ||
          !requestGuid ||
          metadataGuid !== requestGuid
        ) {
          throw new CarePlanLiveWriteBlockedError(
            'Client metadata is unavailable, so the case status cannot be changed.',
          );
        }

        const closedReasonCode =
          options.closedReasonCode ??
          (options.closedReason
            ? toClosedReasonCode(options.closedReason)
            : undefined);
        if (toStatus === 'closed') {
          await this.loadClosedReasons();
          if (
            this._closedReasonsLoading() ||
            this._closedReasonsError() ||
            !this._closedReasons()?.some(
              (item) => item.code === closedReasonCode,
            )
          ) {
            throw new Error('Select an available case closure reason.');
          }
        } else if (closedReasonCode) {
          throw new Error(
            'Closure reasons are only supported when closing a case.',
          );
        }
        if (
          this.sessionGuid() !== requestGuid ||
          this._clientContext?.activeGuid() !== metadataGuid ||
          this._clientContext?.clientId() !== clientId ||
          !this.canEditLiveCases()
        ) {
          throw new CarePlanLiveWriteBlockedError(
            'The active client or edit access changed. Try again.',
          );
        }
        let transitionResource: CaseStatusTransitionResource;
        try {
          transitionResource = await firstValueFrom(
            logAttempt.request(
              this._caseApi.transitionCaseStatus(numericCaseId, {
                toStatusCode: this.statusCode(toStatus),
                expectedCurrentStatusCode: this.statusCode(current.status),
                ...(closedReasonCode ? { closedReasonCode } : {}),
                ...(options.reason?.trim()
                  ? { reason: options.reason.trim() }
                  : {}),
              }),
            ),
          );
        } catch (error) {
          if (caseApiErrorStatus(error) === 409) {
            try {
              const resource = await firstValueFrom(
                this._caseApi.getCase(numericCaseId),
              );
              const refreshed = toCarePlanCase({
                ...resource,
                description: resource.description ?? current.description,
              });
              if (
                this._isLive() &&
                this.sessionGuid() === requestGuid &&
                this._activeGuid() === requestGuid &&
                this._clientContext?.activeGuid() === requestGuid &&
                this._clientContext?.clientId() === clientId
              ) {
                this._cases.update((cases) =>
                  cases.map((item) => (item.id === caseId ? refreshed : item)),
                );
                this._hydrateCaseReferenceLabels();
              }
            } catch {
              // Preserve the original conflict when its recovery read also fails.
            }
          }
          throw error;
        }
        let refreshed: CarePlanCase;
        try {
          const resource = await firstValueFrom(
            this._caseApi.getCase(numericCaseId),
          );
          refreshed = toCarePlanCase({
            ...resource,
            description: resource.description ?? current.description,
          });
        } catch {
          // The transition has already committed. Keep local concurrency state in
          // sync with its response even when the authoritative follow-up read is
          // temporarily unavailable.
          refreshed = applyTransitionFallback(
            current,
            transitionResource,
            toStatus,
            closedReasonCode,
          );
        }
        const transitioned: CarePlanCase = {
          ...refreshed,
          ...(isClosedCaseStatus(toStatus) &&
          !isClosedCaseStatus(current.status)
            ? {
                priorStatus: current.status,
                ...(options.reason?.trim()
                  ? { closedNote: options.reason.trim() }
                  : {}),
              }
            : {}),
        };

        if (
          !this._isLive() ||
          this.sessionGuid() !== requestGuid ||
          this._activeGuid() !== requestGuid ||
          this._clientContext?.activeGuid() !== requestGuid ||
          this._clientContext?.clientId() !== clientId
        ) {
          return transitioned;
        }
        this._cases.update((cases) =>
          cases.map((item) => (item.id === caseId ? transitioned : item)),
        );
        this._hydrateCaseReferenceLabels();
        return this._cases().find((item) => item.id === caseId) ?? transitioned;
      },
    );
  }

  /** @internal Test-only fixture seam retained for isolated component tests. */
  loadSeed(seed: CarePlanSeed): void {
    this._loadGeneration++;
    this._isLive.set(false);
    this._cases.set(seed.cases.map((c) => ({ ...c })));
    this._goals.set(seed.goals.map((g) => ({ ...g })));
    this._loading.set(false);
    this._error.set(null);
    this._loadWarning.set(null);
    this._expandedCaseId.set(null);
  }

  setGroupBy(groupBy: CarePlanGroupBy): void {
    this._groupBy.set(
      groupBy === 'goal' && !this.goalsEnabled() ? 'status' : groupBy,
    );
  }

  /** Expands one case's detail inline, or collapses everything with `null`. */
  setExpandedCase(caseId: string | null): void {
    this._expandedCaseId.set(caseId);
  }

  toggleExpandedCase(caseId: string): void {
    this._expandedCaseId.update((current) =>
      current === caseId ? null : caseId,
    );
  }

  setFilters(filters: Partial<CarePlanCaseFilters>): void {
    this._filters.update((current) => ({
      ...current,
      ...filters,
      ...(!this.goalsEnabled() ? { goalId: null } : {}),
    }));
  }

  resetFilters(): void {
    this._filters.set(DEFAULT_CASE_FILTERS);
  }

  /** An open case (active status) sharing the same Domain+Objective, if any (PRD §4 duplicate detection). */
  findDuplicate(domain: string, objective: string): CarePlanCase | null {
    return (
      this._cases().find(
        (c) =>
          ACTIVE_CASE_STATUSES.includes(c.status) &&
          c.domain === domain &&
          c.objective === objective,
      ) ?? null
    );
  }

  openCase(input: OpenCaseInput): CarePlanCase {
    this._assertFixtureMutation();
    const goalIds = this.goalsEnabled()
      ? [...new Set(input.goalIds ?? [])]
      : [];
    const newCase: CarePlanCase = {
      id: crypto.randomUUID(),
      number: nextCaseNumber(this._cases()),
      title: input.objective,
      createdAt: new Date().toISOString(),
      description: input.description,
      domain: input.domain,
      objective: input.objective,
      status: input.status ?? 'in_progress',
      goalIds,
    };
    this._cases.update((cases) => [...cases, newCase]);
    if (goalIds.length > 0) {
      this._goals.update((goals) =>
        goals.map((g) =>
          goalIds.includes(g.id)
            ? { ...g, linkedCaseIds: [...g.linkedCaseIds, newCase.id] }
            : g,
        ),
      );
    }
    return newCase;
  }

  updateCaseStatus(caseId: string, status: CarePlanCaseStatus): void {
    this._assertFixtureMutation();
    this._cases.update((cases) =>
      cases.map((c) => (c.id === caseId ? { ...c, status } : c)),
    );
  }

  private async _updateLiveCaseDescription(
    caseId: string,
    description: string,
  ): Promise<void> {
    return this._actionLogs.run(
      'care_plan.case.update',
      `${this.sessionGuid()}:${caseId}`,
      async (logAttempt) => {
        logAttempt.identifiers = {
          client_guid: this.sessionGuid(),
          case_id: caseId,
        };
        logAttempt.stage = 'save';
        const value = description.trim();
        const guid = this.sessionGuid();
        const clientId = this._clientContext?.clientId();
        const current = this._cases().find((item) => item.id === caseId);
        if (
          !this._isLive() ||
          !this._caseApi ||
          !this.canEditLiveCases() ||
          !guid ||
          this._activeGuid() !== guid ||
          this._clientContext?.activeGuid() !== guid ||
          !clientId ||
          !current ||
          !Number.isSafeInteger(Number(caseId)) ||
          Number(caseId) <= 0
        ) {
          throw new CarePlanLiveWriteBlockedError(
            'This case cannot be edited right now.',
          );
        }
        if (!value || value.length > 250)
          throw new Error('Enter a description between 1 and 250 characters.');
        if (isClosedCaseStatus(current.status))
          throw new Error('Closed cases cannot be edited.');
        const updated = await firstValueFrom(
          logAttempt.request(
            this._caseApi.updateCase(Number(caseId), { description: value }),
          ),
        );
        if (
          !this._isLive() ||
          !this.canEditLiveCases() ||
          this._clientContext?.activeGuid() !== guid ||
          this.sessionGuid() !== guid ||
          this._clientContext?.clientId() !== clientId ||
          this._activeGuid() !== guid
        )
          return;
        this._cases.update((cases) =>
          cases.map((item) =>
            item.id === caseId
              ? { ...item, description: updated.description ?? value }
              : item,
          ),
        );
      },
    );
  }

  updateCaseDescription(caseId: string, description: string): void {
    this._assertFixtureMutation();
    this._cases.update((cases) =>
      cases.map((c) => (c.id === caseId ? { ...c, description } : c)),
    );
  }

  linkGoal(caseId: string, goalId: string): void {
    if (!this.goalsEnabled()) return;
    this._assertFixtureMutation();
    this._cases.update((cases) =>
      cases.map((c) =>
        c.id === caseId && !c.goalIds.includes(goalId)
          ? { ...c, goalIds: [...c.goalIds, goalId] }
          : c,
      ),
    );
    this._goals.update((goals) =>
      goals.map((g) =>
        g.id === goalId && !g.linkedCaseIds.includes(caseId)
          ? { ...g, linkedCaseIds: [...g.linkedCaseIds, caseId] }
          : g,
      ),
    );
  }

  unlinkGoal(caseId: string, goalId: string): void {
    if (!this.goalsEnabled()) return;
    this._assertFixtureMutation();
    this._cases.update((cases) =>
      cases.map((c) =>
        c.id === caseId
          ? { ...c, goalIds: c.goalIds.filter((id) => id !== goalId) }
          : c,
      ),
    );
    this._goals.update((goals) =>
      goals.map((g) =>
        g.id === goalId
          ? {
              ...g,
              linkedCaseIds: g.linkedCaseIds.filter((id) => id !== caseId),
            }
          : g,
      ),
    );
  }

  closeCase(caseId: string, reason: CarePlanClosedReason, note?: string): void {
    this._assertFixtureMutation();
    this._cases.update((cases) =>
      cases.map((c) =>
        c.id === caseId
          ? {
              ...c,
              priorStatus: c.status,
              status: 'closed',
              closedReason: reason,
              closedNote: note,
              closedAt: new Date().toISOString(),
            }
          : c,
      ),
    );
  }

  /** Restores the prior active status and clears current closure fields (D17). History preservation is deferred. */
  reopenCase(caseId: string): void {
    this._assertFixtureMutation();
    this._cases.update((cases) =>
      cases.map((c) => {
        if (c.id !== caseId || !isClosedCaseStatus(c.status)) return c;
        const restored: CarePlanCase = {
          ...c,
          status: c.priorStatus ?? 'in_progress',
        };
        delete restored.priorStatus;
        delete restored.closedReason;
        delete restored.closedNote;
        delete restored.closedAt;
        return restored;
      }),
    );
  }

  /** Returns null (blocked) when the client is already at the 3-active-goal cap. */
  addGoal(text: string): CarePlanGoal | null {
    if (!this.goalsEnabled()) return null;
    this._assertFixtureMutation();
    if (this.activeGoals().length >= CARE_PLAN_MAX_ACTIVE_GOALS) {
      return null;
    }
    const goal: CarePlanGoal = {
      id: crypto.randomUUID(),
      displayText: text,
      originalDescription: text,
      status: 'active',
      sortOrder: this._goals().length,
      linkedCaseIds: [],
    };
    this._goals.update((goals) => [...goals, goal]);
    return goal;
  }

  /** Never overwrites `originalDescription` (PRD §4). */
  editGoal(goalId: string, displayText: string): void {
    if (!this.goalsEnabled()) return;
    this._assertFixtureMutation();
    this._goals.update((goals) =>
      goals.map((g) => (g.id === goalId ? { ...g, displayText } : g)),
    );
  }

  completeGoal(goalId: string): void {
    if (!this.goalsEnabled()) return;
    this._assertFixtureMutation();
    this._goals.update((goals) =>
      goals.map((g) => (g.id === goalId ? { ...g, status: 'achieved' } : g)),
    );
  }

  removeGoal(goalId: string): void {
    if (!this.goalsEnabled()) return;
    this._assertFixtureMutation();
    this._goals.update((goals) =>
      goals.map((g) => (g.id === goalId ? { ...g, status: 'abandoned' } : g)),
    );
  }

  reorderGoals(orderedActiveGoalIds: string[]): void {
    if (!this.goalsEnabled()) return;
    if (this._isLive() && !this.canManageGoals()) return;
    this._goals.update((goals) =>
      goals.map((g) => {
        const index = orderedActiveGoalIds.indexOf(g.id);
        return index === -1 ? g : { ...g, sortOrder: index };
      }),
    );
    const guid = this._activeGuid();
    const clientId = this._clientContext?.clientId();
    if (this._isLive() && guid && clientId)
      this._goalCache.write(guid, clientId, this._goals());
  }

  private _reset(): void {
    this._loadGeneration++;
    this._activeGuid.set(null);
    this._cases.set([]);
    this._resetGoals();
    this._loading.set(false);
    this._error.set(null);
    this._loadWarning.set(null);
    this._filters.set(DEFAULT_CASE_FILTERS);
    this._expandedCaseId.set(null);
  }

  private _prepareLiveLoad(guid: string): void {
    const clientChanged = this._activeGuid() !== guid;
    if (clientChanged) {
      this._cases.set([]);
      this._resetGoals();
      this._filters.set(DEFAULT_CASE_FILTERS);
      this._expandedCaseId.set(null);
    }
    this._activeGuid.set(guid);
    this._error.set(null);
    this._loadWarning.set(null);
    this._loading.set(true);
  }

  private _setLiveError(
    generation: number,
    guid: string,
    message: string,
  ): void {
    if (generation !== this._loadGeneration || this._activeGuid() !== guid)
      return;
    this._loading.set(false);
    this._error.set({ kind: 'unrecoverable', message });
  }

  private _mergeCaseSlice(
    incoming: readonly CarePlanCase[],
    statusFilter: CarePlanCaseStatus | null,
  ): CarePlanCase[] {
    // The unfiltered response is authoritative; do not retain old filtered slices.
    if (!statusFilter) return [...incoming];
    const refreshedStatuses = new Set<CarePlanCaseStatus>([statusFilter]);
    const merged = new Map<string, CarePlanCase>();
    incoming.forEach((caseRecord) => merged.set(caseRecord.id, caseRecord));
    this._cases()
      .filter((caseRecord) => !refreshedStatuses.has(caseRecord.status))
      .forEach((caseRecord) => {
        if (!merged.has(caseRecord.id)) merged.set(caseRecord.id, caseRecord);
      });
    return [...merged.values()];
  }

  private async _loadCaseSlice(
    clientId: number,
    statusFilter: CarePlanCaseStatus | null,
  ): Promise<CaseResource[]> {
    const wireStatuses = statusFilter ? [this.statusCode(statusFilter)] : null;
    if (!wireStatuses) return this._loadAllCasePages(clientId);

    const pages = await Promise.all(
      wireStatuses.map((statusCode) =>
        this._loadAllCasePages(clientId, statusCode),
      ),
    );
    const resources = new Map<number, CaseResource>();
    pages
      .flat()
      .forEach((resource) => resources.set(resource.caseId, resource));
    return [...resources.values()];
  }

  private async _loadAllCasePages(
    clientId: number,
    statusCode?: CaseStatusCode,
  ): Promise<CaseResource[]> {
    if (!this._caseApi) return [];

    const resources = new Map<number, CaseResource>();
    const visitedOffsets = new Set<number>();
    let offset = 0;

    while (true) {
      if (visitedOffsets.has(offset)) {
        throw new Error('Case API pagination did not advance.');
      }
      visitedOffsets.add(offset);

      const page = await firstValueFrom(
        this._caseApi.queryCases({
          clientId,
          ...(statusCode ? { statusCode } : {}),
          limit: 100,
          offset,
        }),
      );
      page.cases.forEach((resource) =>
        resources.set(resource.caseId, resource),
      );

      if (page.resultSet.allFetched) return [...resources.values()];

      const reportedOffset = page.resultSet.offset;
      const fallbackOffset = offset + page.cases.length;
      const nextOffset =
        reportedOffset > offset ? reportedOffset : fallbackOffset;
      if (nextOffset <= offset) {
        throw new Error('Case API pagination did not advance.');
      }
      offset = nextOffset;
    }
  }

  private _groupingDomains(): readonly CarePlanDomain[] {
    if (!this._isLive()) return this._fixtureTaxonomy?.domains ?? [];
    return this._domains().map((domain) => ({
      key: `domain-id:${domain.domainId}`,
      label: domain.shortName || domain.name,
      order: domain.displayOrder,
    }));
  }

  private _hydrateCaseReferenceLabels(): void {
    const objectivesById = new Map(
      Object.values(this._objectiveTypes())
        .flat()
        .map((objective) => [objective.objectiveTypeId, objective]),
    );
    this._cases.update((cases) =>
      cases.map((caseRecord) => {
        const objective = caseRecord.objectiveTypeId
          ? objectivesById.get(caseRecord.objectiveTypeId)
          : undefined;
        const objectiveLabel =
          objective?.name ||
          objective?.clientFacingName ||
          objective?.plainLanguageName;
        return {
          ...caseRecord,
          domain: caseRecord.domainId
            ? `domain-id:${caseRecord.domainId}`
            : caseRecord.domain,
          title: objectiveLabel || caseRecord.title,
          objective: objectiveLabel || caseRecord.objective,
        };
      }),
    );
  }

  private _assertFixtureMutation(): void {
    if (this._isLive()) {
      throw new CarePlanLiveWriteBlockedError(
        'This change is waiting on AIM domain/objective reference data.',
      );
    }
  }
}

function toCarePlanCase(resource: CaseResource): CarePlanCase {
  const status = toCarePlanStatus(resource.statusCode);
  const domainId = resource.domainId ?? resource.domain?.domainId;
  const objectiveTypeId =
    resource.objectiveTypeId ?? resource.objectiveType?.objectiveTypeId;
  const objectiveName = resource.objectiveType?.name?.trim();
  if (!status) {
    throw new Error(`Unsupported AIM case status: ${resource.statusCode}`);
  }
  return {
    id: String(resource.caseId),
    number: `CASE-${resource.caseId}`,
    title:
      objectiveName ||
      (objectiveTypeId
        ? `Objective type ID ${objectiveTypeId}`
        : 'Objective type unavailable'),
    createdAt: resource.openedAt,
    description:
      resource.description?.trim() ||
      resource.reason?.trim() ||
      'Description unavailable from the Case API.',
    domain: domainId ? `domain-id:${domainId}` : 'domain-unavailable',
    objective:
      objectiveName ||
      (objectiveTypeId
        ? `Objective type ID ${objectiveTypeId}`
        : 'Objective type unavailable'),
    status,
    goalIds: [],
    domainId,
    objectiveTypeId,
    checkInCadenceDays: resource.checkInCadenceDays,
    ...(resource.closedReasonCode
      ? {
          closedReasonCode: resource.closedReasonCode,
          closedReason: toClosedReason(resource.closedReasonCode),
        }
      : {}),
    ...(resource.closedAt ? { closedAt: resource.closedAt } : {}),
  };
}

function toCarePlanStatus(
  statusCode: CaseResource['statusCode'],
): CarePlanCaseStatus | null {
  return statusCode?.trim() ? statusCode.toLowerCase() : null;
}

function isSupportedCaseStatus(
  statusCode: CaseResource['statusCode'],
): boolean {
  return toCarePlanStatus(statusCode) !== null;
}

function applyTransitionFallback(
  current: CarePlanCase,
  resource: CaseStatusTransitionResource,
  requestedStatus: CarePlanCaseStatus,
  requestedClosedReasonCode?: string,
): CarePlanCase {
  const status = toCarePlanStatus(resource.statusCode) ?? requestedStatus;
  const withoutClosure = { ...current };
  delete withoutClosure.closedReason;
  delete withoutClosure.closedReasonCode;
  delete withoutClosure.closedAt;
  delete withoutClosure.closedNote;
  if (status !== 'closed') return { ...withoutClosure, status };

  const closedReasonCode =
    resource.closedReasonCode ?? requestedClosedReasonCode;
  return {
    ...current,
    status,
    ...(closedReasonCode
      ? {
          closedReasonCode,
          closedReason: toClosedReason(closedReasonCode),
        }
      : {}),
    ...(resource.closedAt ? { closedAt: resource.closedAt } : {}),
  };
}

function toClosedReason(code: string): CarePlanClosedReason | undefined {
  const suffix = code.replace(/^CASE_CLOSED_REASON_/, '').toLowerCase();
  if (suffix === 'achieved' || suffix === 'complete' || suffix === 'completed')
    return 'achieved';
  if (suffix === 'abandoned') return 'abandoned';
  if (suffix === 'no_longer_eligible') return 'no_longer_eligible';
  if (suffix === 'cancelled' || suffix === 'canceled') return 'cancelled';
  return undefined;
}

function toClosedReasonCode(reason: CarePlanClosedReason): string {
  const codes: Record<CarePlanClosedReason, string> = {
    achieved: 'COMPLETED',
    abandoned: 'ABANDONED',
    no_longer_eligible: 'NO_LONGER_ELIGIBLE',
    cancelled: 'CANCELED',
  };
  return codes[reason];
}

function nextCaseNumber(existing: readonly CarePlanCase[]): string {
  const max = existing.reduce((acc, c) => {
    const match = /^CASE-(\d+)$/.exec(c.number);
    return match ? Math.max(acc, Number(match[1])) : acc;
  }, 0);
  return `CASE-${String(max + 1).padStart(4, '0')}`;
}
